/**
 * compliance.ts — the two regulatory controls a CBD retailer cannot ship without:
 * a 21+ age affirmation, and a destination-state eligibility check.
 *
 * DRB-4 specified both. Neither existed: a repo-wide search on 2026-08-17 returned 0 matches for
 * every plausible spelling of an age gate (age / ageVerified / dob / birthdate / over21 / 21+) and
 * 0 matches for any allow- or deny-list of states. The `state` field was a free-text <Input>
 * defaulted to 'AZ' that flowed unchecked into Square's `administrativeDistrictLevel1`.
 *
 * ── WHO OWNS THE LIST ──────────────────────────────────────────────────────────────────────
 * The DEFAULT below is a conservative engineering seed, NOT legal advice, and it is deliberately
 * overridable from the admin panel (siteSettings.restrictedShippingStates) so counsel can change
 * it without a deploy. Hemp-CBD shipping law moves faster than release cycles: sources surveyed
 * 2026-08-17 agree on Idaho, Nebraska and South Dakota, disagree about Kansas, and Tennessee's
 * HB 1376 banned online delivery of hemp cannabinoid products to TN addresses from 2026-01-01.
 * That spread is exactly why this is a data field and not a constant in a bundle.
 *
 * ── WHY IT LIVES IN TWO PLACES ─────────────────────────────────────────────────────────────
 * functions/src/lib/compliance.ts is the byte-identical server mirror. They are one rule
 * expressed twice, in two TypeScript projects that cannot typecheck each other — precisely the
 * configuration that produced DRB-30 (client and server disagreed on shipping for ~90 minutes and
 * every order over $100 was unbuyable). scripts/check-compliance-parity.mjs asserts they agree
 * and FAILS the build if they drift. Change one, change the other in the same commit.
 */

export const MIN_AGE = 21;

/** localStorage key for the age affirmation. Versioned so the prompt can be re-asked. */
export const AGE_STORAGE_KEY = "drb_age_affirmed_v1";

/**
 * How long an affirmation stands before we ask again. A permanent flag means a shared or
 * second-hand device is affirmed forever by whoever touched it first.
 */
export const AGE_TTL_DAYS = 30;

/**
 * Conservative seed. NOT legal advice — see the header. Counsel ratifies; admin edits.
 *   ID, NE, SD — the three every surveyed source agrees restrict hemp-derived CBD retail.
 *   KS         — several sources classify CBD as unlawful; included because the cost of an
 *                over-broad default is a lost sale, and of an under-broad one is a shipped
 *                federal-mail violation. Asymmetric, so we err toward refusing.
 * Tennessee is NOT seeded: its restriction is on online DELIVERY of hemp cannabinoid products,
 * which may or may not reach this catalogue. Flagged for counsel rather than guessed at.
 */
export const DEFAULT_RESTRICTED_STATES: readonly string[] = [
  "ID",
  "KS",
  "NE",
  "SD",
];

export const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

const BY_NAME: ReadonlyMap<string, string> = new Map(
  US_STATES.map((s) => [s.name.toLowerCase(), s.code]),
);
const CODES: ReadonlySet<string> = new Set(US_STATES.map((s) => s.code));

/**
 * Accept what a human actually types and return a canonical 2-letter code, or '' if it is not a
 * US state. The old field was free text, so real orders can carry 'arizona', ' az ', or 'Ariz'.
 * A restriction check that only matches an exact uppercase code is trivially bypassed by typing
 * the state's name — which is why normalisation is part of the control, not a convenience.
 */
export function normalizeState(input: unknown): string {
  if (typeof input !== "string") return "";
  const t = input.trim();
  if (!t) return "";
  const upper = t.toUpperCase();
  if (upper.length === 2 && CODES.has(upper)) return upper;
  return BY_NAME.get(t.toLowerCase()) ?? "";
}

export type ShipEligibility =
  | { ok: true; state: string }
  | { ok: false; reason: "unrecognized"; state: "" }
  | { ok: false; reason: "restricted"; state: string };

/**
 * THE gate. Three outcomes, never two — an unrecognised state is not the same as a permitted one,
 * and collapsing it into `ok` is how a typo becomes an unchecked shipment.
 *
 * Fails CLOSED: anything we cannot positively recognise as a permitted US state is refused.
 */
export function checkShipEligibility(
  rawState: unknown,
  restricted: readonly string[] = DEFAULT_RESTRICTED_STATES,
): ShipEligibility {
  const state = normalizeState(rawState);
  if (!state) return { ok: false, reason: "unrecognized", state: "" };
  const denied = new Set(restricted.map((s) => String(s).trim().toUpperCase()));
  if (denied.has(state)) return { ok: false, reason: "restricted", state };
  return { ok: true, state };
}

/** Customer-facing copy. Never leaks the list itself, and never blames the customer. */
export function shipRestrictionMessage(r: ShipEligibility): string | null {
  if (r.ok) return null;
  if (r.reason === "unrecognized")
    return "Please choose a state from the list.";
  const name = US_STATES.find((s) => s.code === r.state)?.name ?? r.state;
  return (
    `We're not able to ship to ${name} right now. State law on hemp-derived CBD varies, and ` +
    `${name} is one we cannot deliver to. Please contact us if you think this is a mistake.`
  );
}

// ── Age affirmation ─────────────────────────────────────────────────────────────────────────

/**
 * Read the stored affirmation. Returns false on anything unexpected — an unparseable or expired
 * record is treated as "not affirmed", never as affirmed, so a corrupted key cannot open the gate.
 * Wrapped because localStorage throws outright in Safari private mode.
 */
export function hasAffirmedAge(now: number = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(AGE_STORAGE_KEY);
    if (!raw) return false;
    const rec = JSON.parse(raw) as { ok?: unknown; at?: unknown };
    if (rec.ok !== true || typeof rec.at !== "number") return false;
    return now - rec.at < AGE_TTL_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

export function recordAgeAffirmation(now: number = Date.now()): void {
  try {
    localStorage.setItem(
      AGE_STORAGE_KEY,
      JSON.stringify({ ok: true, at: now }),
    );
  } catch {
    /* private mode — the gate simply re-asks next visit, which is the safe direction */
  }
}
